<template>
  <section id="FactSheetCreatePage" class="section">
    <!-- PAGE HEADER -->
    <div class="columns is-multiline is-vcentered is-mobile has-text-left">
      <!-- Back Button -->
      <div class="column is-1">
        <div @click.prevent="navigateToFactsheetMain">
          <b-icon class="clickable-icon" icon="chevron-left" size="is-medium" />
        </div>
      </div>
      <!-- Title -->
      <div class="column is-11-desktop is-11-tablet is-12-mobile">
        <h1 v-if="isEditMode" class="title">UPDATE FACTSHEET</h1>
        <h1 v-else class="title">CREATE NEW FACTSHEET</h1>
      </div>
    </div>
    <!-- END : PAGE HEADER -->
    <div class="container">
      <!-- CONTENT BODY-->
      <div style="margin-top: 1.5rem">
        <b-progress
          v-if="!isEditMode"
          :value="formPercentCompleted"
          type="is-success"
          size="is-medium"
          show-value
        >
          {{ activeStep + 1 }} out of 6
        </b-progress>
        <b-steps
          v-if="!isEditMode || (isEditMode && isFactsheetLoaded)"
          v-model="activeStep"
          :has-navigation="false"
        >
          <b-step-item label="ข้อมูลเบื้องต้น" :clickable="isEditMode">
            <fact-customer-info
              :edited="isEditMode"
              @form_completed="forwardActiveStep()"
              @existing_sheet_updated="requestUpsertFactSheet()"
            />
          </b-step-item>

          <b-step-item label="แผนการเกษียณ" :clickable="isEditMode">
            <fact-retirement-plan
              :edited="isEditMode"
              @back="backwardActiveStep()"
              @form_completed="forwardActiveStep()"
              @existing_sheet_updated="requestUpsertFactSheet()"
            />
          </b-step-item>

          <b-step-item label="แผนการศึกษาบุตร" :clickable="isEditMode">
            <fact-education-plan
              :edited="isEditMode"
              @back="backwardActiveStep()"
              @form_completed="forwardActiveStep()"
              @existing_sheet_updated="requestUpsertFactSheet()"
            />
          </b-step-item>

          <b-step-item label="วางแผนภาษี" :clickable="isEditMode">
            <fact-tax-plan
              :edited="isEditMode"
              @back="backwardActiveStep()"
              @form_completed="forwardActiveStep()"
              @existing_sheet_updated="requestUpsertFactSheet()"
            />
          </b-step-item>

          <b-step-item label="ประกันสุขภาพ" :clickable="isEditMode">
            <fact-health-plan
              :edited="isEditMode"
              @back="backwardActiveStep()"
              @form_completed="forwardActiveStep()"
              @existing_sheet_updated="requestUpsertFactSheet()"
            />
          </b-step-item>
          <b-step-item label="ทุนปกป้องครอบครัว" :clickable="isEditMode">
            <fact-family-plan
              :edited="isEditMode"
              @back="backwardActiveStep()"
              @form_completed="forwardActiveStep()"
              @existing_sheet_updated="requestUpsertFactSheet()"
            />
          </b-step-item>
        </b-steps>
      </div>
      <!-- END : CONTENT BODY-->
    </div>
  </section>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";

import FactsheetApi from "@/services/FactsheetApi";
import AlertManager from "@/utils/AlertManager";

import FactCustomerInfo from "@/components/factsheet/FactCustomerInfo.vue";
import FactRetirementPlan from "@/components/factsheet/FactRetirementPlan.vue";
import FactEducationPlan from "@/components/factsheet/FactEducationPlan.vue";
import FactTaxPlan from "@/components/factsheet/FactTaxPlan.vue";
import FactHealthPlan from "@/components/factsheet/FactHealthPlan.vue";
import FactFamilyPlan from "@/components/factsheet/FactFamilyPlan.vue";
import FinalRemarkForm from "@/components/forms/FinalRemarkForm.vue";

import AlertEditing from "@/components/forms/AlertEditing.vue";

export default {
  name: "FactSheetCreatePage",
  components: {
    FactCustomerInfo,
    FactRetirementPlan,
    FactEducationPlan,
    FactTaxPlan,
    FactHealthPlan,
    FactFamilyPlan
  },
  computed: {
    ...mapGetters(["combinded_factsheet"]),
    client_id: ({ $route }) => $route.query.clientId,
    factsheet_id: ({ $route }) => $route.query.factsheetId,
    isEditMode: ({ $route }) => ($route.query.factsheetId ? true : false),
    formPercentCompleted: function() {
      return ((this.activeStep + 1) / 6) * 100;
    }
  },
  data() {
    return {
      activeStep: 0,
      isByPassPopUp: false,
      isFactsheetLoaded: false
    };
  },
  methods: {
    ...mapMutations({
      setExistingClientId: "SET_CLIENT_ID",
      setExistingFactSheet: "SET_EXISTING_FACTSHEET",
      clearFactSheetProperties: "CLEAR_FACTSHEET_PROPERTIES"
    }),
    presentRemarkForm: function() {
      this.$buefy.modal.open({
        parent: this,
        hasModalCard: true,
        component: FinalRemarkForm,
        props: { edited: this.isEditMode },
        events: {
          completed_form: () => {
            this.requestUpsertFactSheet();
          },
          existing_sheet_updated: () => {
            this.requestUpsertFactSheet();
          }
        }
      });
    },
    forwardActiveStep: function() {
      if (this.activeStep >= 0 && this.activeStep < 5) {
        this.activeStep += 1;
      } else if (this.activeStep === 5) {
        this.presentRemarkForm();
      }
    },
    backwardActiveStep: function() {
      if (this.activeStep > 0 && this.activeStep <= 5) {
        this.activeStep -= 1;
      }
    },
    navigateToFactsheetMain: function() {
      this.$router.push({ name: "factsheet" });
    },
    requestGetFactSheetDetail: async function(fact_id) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      const response = await FactsheetApi.requestFactsheetById(fact_id);
      loadingComponent.close();
      if (response.status && response.payload) {
        this.isFactsheetLoaded = true;
        this.setExistingFactSheet(response.payload);
      } else {
        AlertManager.showErrorSnackbar(response.message);
      }
    },
    requestUpsertFactSheet: async function() {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      const response = await FactsheetApi.requestCreateNewFactsheet(
        this.combinded_factsheet
      );
      loadingComponent.close();
      if (response.status) {
        this.isByPassPopUp = true;
        AlertManager.showSuccessSnackbar(response.message);
        this.navigateToFactsheetMain();
      } else {
        AlertManager.showErrorSnackbar(response.message);
      }
    }
  },
  async beforeMount() {
    if (this.isEditMode) {
      await this.requestGetFactSheetDetail(this.factsheet_id);
    } else {
      if (this.client_id) {
        this.setExistingClientId(this.client_id);
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (!this.isByPassPopUp) {
      this.$buefy.modal.open({
        parent: this,
        hasModalCard: true,
        component: AlertEditing,
        events: {
          confirmed: () => {
            next();
          },
          rejected: () => {
            next(false);
          }
        }
      });
    } else {
      next();
    }
  },
  beforeDestroy() {
    this.clearFactSheetProperties();
  }
};
</script>
